import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Coins, ShieldCheck, CheckCircle2, TrendingDown, ChevronDown, Info, Sparkles } from "lucide-react";
import { DashboardShell, PageHead } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { journeyApi, apiError } from "@/lib/journeyApi";
import { fmt } from "@/lib/format";
import { celebrate } from "@/lib/celebrate";
import { track } from "@/lib/analytics";

const input = "mt-1 w-full rounded-xl pe-surface px-3.5 py-2.5 text-base text-[#171717] outline-none focus:border-[#2F6B57] focus:ring-2 focus:ring-[#2F6B57]/25";
const money = (v, set, tid) => (
  <input inputMode="decimal" value={v} data-testid={tid} onChange={(e) => set(e.target.value.replace(/[^0-9.]/g, ""))} className={input} placeholder="$0" />
);
const numv = (v) => { const n = parseFloat(v); return Number.isFinite(n) && n > 0 ? n : 0; };

// A legitimately small amount of available money is still a real move. Above
// this it reads as an ordinary Sprint. This is presentation only; it never
// changes any financial calculation.
const SMALL_EDIT_MAX = 100;

// Deterministic, plain-language reason for the proposed move, derived ONLY from
// the numbers the canonical engine already returned. No generative reasoning.
// It reconciles exactly with "See the math".
function whyThisMove(s) {
  const b = s.foundation_breakdown || {};
  const prot = b.protection || 0;
  const pot = s.potential_sprint_money || 0;
  const obl = b.total_obligations || 0;
  if (s.sprint_type === "protection" || (prot > 0 && pot === 0)) {
    return `Real life and your Protected Runway come first, so this whole event goes to protection. ${fmt(prot)} moves your runway forward. Protection is progress, too.`;
  }
  if (pot === 0) {
    return `After covering ${fmt(obl)} of real-life obligations${prot > 0 ? ` and ${fmt(prot)} of protection` : ""}, nothing is left over this time. That is a complete, healthy answer, not a miss.`;
  }
  if (Array.isArray(s.allocations) && s.allocations.length > 0) {
    const first = s.allocations[0];
    let base = `${fmt(b.income_received)} landed. After ${fmt(obl)} for real life${prot > 0 ? ` and ${fmt(prot)} protected` : ""}, ${fmt(pot)} is free to attack debt. ${first.name} has the smallest balance, so it gets hit first${first.eliminated ? " and clears" : ""}.`;
    if (s.allocations.length > 1) base += " Whatever is left rolls to the next smallest balance automatically.";
    return base;
  }
  if (s.sprint_type === "build") {
    return `No eligible debt remains, so ${fmt(pot)} is free to build. ${fmt(s.build_recommended || 0)} is proposed toward ${s.goal_name || "your goal"}${(s.unassigned || 0) > 0 ? `, and ${fmt(s.unassigned)} stays unassigned until you choose where it goes` : ""}.`;
  }
  if (s.sprint_type === "build_unassigned") {
    return `No debt left and no goal chosen yet, so all ${fmt(pot)} stays unassigned until you give it a job. Keeping it unassigned is a perfectly good choice.`;
  }
  return `${fmt(pot)} is available after your real-life obligations.`;
}

const DRAFT_KEY = "ms_money_landed_draft";
const loadDraft = () => { try { return JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "{}"); } catch { return {}; } };
const clearDraft = () => { try { sessionStorage.removeItem(DRAFT_KEY); } catch {} };

const CHANGE_OPTIONS = [
  { key: "none", label: "Everything looks right" },
  { key: "extra_expense", label: "Unexpected expense" },
  { key: "bill", label: "Major bill changed" },
  { key: "living", label: "Adjust everyday spending" },
  { key: "additional_income", label: "Additional income" },
];

export default function MoneyLanded() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [foundation, setFoundation] = useState(null);
  const [debts, setDebts] = useState([]);
  const [step, setStep] = useState("loading"); // loading|entry|adjust|reveal|confirm|done|whatif
  const [sprint, setSprint] = useState(null);
  const [busy, setBusy] = useState(false);

  const [actual, setActual] = useState(() => loadDraft().actual || "");
  const [landedDate, setLandedDate] = useState(() => loadDraft().landedDate || new Date().toISOString().slice(0, 10));
  const [change, setChange] = useState(() => loadDraft().change || "none");
  const [showOverride, setShowOverride] = useState(false);
  const [adj, setAdj] = useState(() => loadDraft().adj || { additional_income: "", extra_expense: "", extra_expense_name: "", living_override: "", bill_essential_id: "", bill_amount: "", persist: false });
  const [payments, setPayments] = useState([]); // [{debt_id, name, actual_amount}]
  const [buildAmount, setBuildAmount] = useState("");
  const [doneInfo, setDoneInfo] = useState(null); // milestone context for the "done" screen
  // B7: set when the customer taps "More money showed up" so the fresh event
  // carries prior same-cycle reservations forward (no double-reserve).
  const [continueCycle, setContinueCycle] = useState(false);
  // What If (hypothetical). whatIfPlan is a NON-persisted simulation; abandoning
  // it changes nothing. whatIfFromHome controls where NEVER MIND returns.
  const [whatIfAmount, setWhatIfAmount] = useState("");
  const [whatIfPlan, setWhatIfPlan] = useState(null);
  const [whatIfFromHome, setWhatIfFromHome] = useState(false);

  // Persist the in-progress Money Landed entry so Back / refresh / browser
  // back-forward returning to this route never loses the entered amount. The
  // draft is cleared only when the event completes or is explicitly abandoned.
  useEffect(() => {
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ actual, landedDate, change, adj })); } catch {}
  }, [actual, landedDate, change, adj]);

  useEffect(() => { track("money_landed_opened"); }, []);

  const income = foundation?.income || {};
  const isVariable = income.type === "variable";
  const expected = income.take_home_paycheck || 0;

  useEffect(() => {
    journeyApi.sprintState().then((s) => {
      setFoundation(s.foundation);
      setDebts(s.debts || []);
      const active = s.active_sprint;
      if (active && active.status === "awaiting_confirmation") {
        setSprint(active);
        if (active.sprint_type === "build") {
          setBuildAmount(String(active.build_recommended || active.potential_sprint_money || 0));
          setStep("confirm-build");
        } else {
          setPayments(active.allocations.map((a) => ({ debt_id: a.debt_id, name: a.name, actual_amount: String(a.recommended) })));
          setStep("confirm");
        }
      } else if (active && active.status === "recommended") {
        setSprint(active);
        setStep("reveal");
      } else {
        setActual((prev) => prev || (!isVariableType(s.foundation) && s.foundation?.income?.take_home_paycheck ? String(s.foundation.income.take_home_paycheck) : ""));
        if (searchParams.get("whatif") === "1") { setWhatIfFromHome(true); setStep("whatif"); }
        else setStep("entry");
      }
    }).catch(() => { toast.error("Could not load your foundation."); navigate("/dashboard"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear the saved entry draft once the event reaches a terminal state.
  useEffect(() => {
    if (["done", "protected", "done-build"].includes(step)) clearDraft();
  }, [step]);

  // Every new step renders from the top of its content (mobile especially never
  // inherits the previous step's scroll position).
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [step]);

  function isVariableType(f) { return (f?.income?.type) === "variable"; }

  // Adjustments are driven by the `adj` object (populated either by the canonical
  // Life Happened flow handoff or restored from a draft), NOT by a second category
  // list. This keeps ONE canonical change system (Life Happened).
  const buildAdjustments = () => {
    const a = {};
    if (numv(adj.additional_income) > 0) a.additional_income = numv(adj.additional_income);
    if (numv(adj.extra_expense) > 0) {
      a.extra_expense = numv(adj.extra_expense);
      a.extra_expense_name = adj.extra_expense_name || "Unexpected expense";
      a.extra_expense_persist = false;
    }
    if (adj.living_override !== "" && adj.living_override != null) {
      a.living_override = numv(adj.living_override);
      a.living_persist = !!adj.persist;
    }
    if (adj.bill_essential_id && adj.bill_amount !== "") {
      a.bill_overrides = [{ essential_id: adj.bill_essential_id, amount: numv(adj.bill_amount) }];
      a.bill_persist = !!adj.persist;
    }
    return Object.keys(a).length ? a : null;
  };

  const hasAdjustment = () => {
    return numv(adj.additional_income) > 0 || numv(adj.extra_expense) > 0 ||
      (adj.living_override !== "" && adj.living_override != null) ||
      (adj.bill_essential_id && adj.bill_amount !== "");
  };
  const adjustmentLabel = () => {
    if (numv(adj.additional_income) > 0) return `Extra income ${fmt(numv(adj.additional_income))}`;
    if (numv(adj.extra_expense) > 0) return `${adj.extra_expense_name || "Unexpected expense"} ${fmt(numv(adj.extra_expense))}`;
    if (adj.living_override !== "" && adj.living_override != null) return `Everyday spending ${fmt(numv(adj.living_override))}`;
    if (adj.bill_essential_id && adj.bill_amount !== "") return `Bill change ${fmt(numv(adj.bill_amount))}`;
    return "";
  };
  const clearAdjustment = () => setAdj({ additional_income: "", extra_expense: "", extra_expense_name: "", living_override: "", bill_essential_id: "", bill_amount: "", persist: false });

  const doCalculate = async () => {
    const landed = numv(actual);
    if (landed <= 0) return toast.error("Enter the amount that actually landed.");
    setBusy(true);
    try {
      const sp = await journeyApi.calculateSprint({ landed_money: landed, income_date: landedDate, adjustments: buildAdjustments(), continue_cycle: continueCycle });
      setContinueCycle(false);
      setSprint(sp);
      setStep("reveal");
    } catch (e) { toast.error(apiError(e, "Could not calculate your Edit.")); }
    finally { setBusy(false); }
  };

  // B7: a $0 Edit closes ONLY that event. "More money showed up" starts a FRESH
  // canonical Money Landed event without reopening or mutating the prior one.
  // The prior (still-active) recommendation is superseded automatically by the
  // next calculate; its reserved occurrences carry forward (continue_cycle) so
  // obligations and the Living Bucket are never double-reserved.
  const moreMoneyShowedUp = () => {
    clearDraft();
    setSprint(null);
    setActual("");
    setLandedDate(new Date().toISOString().slice(0, 10));
    clearAdjustment();
    setPayments([]);
    setBuildAmount("");
    setDoneInfo(null);
    setContinueCycle(true);
    setStep("entry");
  };

  // B8: KEEP IT UNASSIGNED FOR NOW preserves the money as a canonical source
  // (non-financial state) so it never disappears and stays assignable later.
  const doKeepUnassigned = async () => {
    setBusy(true);
    try { if (sprint?.id) await journeyApi.keepUnassigned(sprint.id); }
    catch (e) { /* non-blocking: still return home */ }
    finally { setBusy(false); navigate("/dashboard"); }
  };

  // What If: NON-persisting simulation of the canonical engine. Nothing is
  // created or changed until the customer explicitly chooses MAKE THIS MY MOVE.
  const runWhatIf = async (amountStr) => {
    const amt = numv(amountStr);
    if (amt <= 0) return toast.error("Enter an amount to explore.");
    setBusy(true);
    try {
      const plan = await journeyApi.simulateSprint({ landed_money: amt, income_date: landedDate });
      setWhatIfPlan(plan);
      track("what_if_simulated");
    } catch (e) { toast.error(apiError(e, "Could not run that scenario.")); }
    finally { setBusy(false); }
  };

  // Explicit transition from a hypothetical into the REAL workflow. This is the
  // ONLY path from What If toward a recommendation, and it still goes through
  // the normal recommended -> accept -> awaiting -> confirm architecture.
  const makeThisMyMove = async () => {
    const amt = whatIfPlan?.landed_money || numv(whatIfAmount);
    if (amt <= 0) return;
    setBusy(true);
    try {
      const sp = await journeyApi.calculateSprint({ landed_money: amt, income_date: landedDate });
      setSprint(sp);
      setActual(String(amt));
      setWhatIfPlan(null); setWhatIfAmount("");
      setStep("reveal");
    } catch (e) { toast.error(apiError(e, "Could not calculate your Edit.")); }
    finally { setBusy(false); }
  };

  const neverMindWhatIf = () => {
    setWhatIfPlan(null); setWhatIfAmount("");
    if (whatIfFromHome) navigate("/dashboard"); else setStep("entry");
  };

  // PLANS CHANGED — the customer revises an UNCONFIRMED decision. The pending
  // recommendation is cancelled (audit preserved, never silently edited, never
  // written to confirmed history); the customer returns to choose again.
  const doChangeMove = async () => {
    setBusy(true);
    try {
      if (sprint?.id) await journeyApi.cancelSprint(sprint.id);
      setSprint(null);
      setPayments([]);
      setStep("adjust");
      toast.message("Plans changed. Let's redo this move.", { description: "Your previous choice was set aside. Nothing was confirmed and your history is unchanged." });
    } catch (e) { toast.error(apiError(e, "Could not change your move.")); }
    finally { setBusy(false); }
  };

  const doAccept = async () => {
    setBusy(true);
    try {
      const sp = await journeyApi.acceptSprint(sprint.id);
      setSprint(sp);
      if (sp.sprint_type === "build") {
        setBuildAmount(String(sp.build_recommended || sp.potential_sprint_money || 0));
        setStep("confirm-build");
      } else {
        setPayments(sprint.allocations.map((a) => ({ debt_id: a.debt_id, name: a.name, actual_amount: String(a.recommended) })));
        setStep("confirm");
      }
    } catch (e) { toast.error(apiError(e, "Could not accept.")); }
    finally { setBusy(false); }
  };

  const doProtect = async () => {
    setBusy(true);
    try {
      const sp = await journeyApi.protectSprint(sprint.id);
      setSprint(sp); setShowOverride(false); setStep("protected");
      celebrate((sp.protection_percent_after || 0) >= 100 ? "runway_complete" : "protection");
    }
    catch (e) { toast.error(apiError(e, "Could not protect.")); }
    finally { setBusy(false); }
  };

  const doProtectOptIn = async () => {
    const landed = numv(actual);
    setBusy(true);
    try {
      const sp = await journeyApi.calculateSprint({ landed_money: landed, income_date: landedDate, adjustments: buildAdjustments(), protect_opt_in: true });
      setSprint(sp);
      setStep("reveal");
      toast.message("Protecting this money.", { description: "We've worked out how much goes to your Protected Runway. Confirm below. The Paycheck Edit never moves your money." });
    } catch (e) { toast.error(apiError(e, "Could not recalculate your Edit.")); }
    finally { setBusy(false); }
  };

  const doOverride = async () => {
    const landed = numv(actual);
    setBusy(true);
    try {
      const sp = await journeyApi.calculateSprint({ landed_money: landed, income_date: landedDate, adjustments: buildAdjustments(), protect_override: true });
      setSprint(sp);
      setShowOverride(false);
      setStep("reveal");
      toast.message("Moving protected money", { description: "You chose to move money The Paycheck Edit recommended keeping protected. Here is exactly where it could go. You still confirm before anything moves." });
    } catch (e) { toast.error(apiError(e, "Could not recalculate your Edit.")); }
    finally { setBusy(false); }
  };

  const doConfirm = async () => {
    const pays = payments.filter((p) => numv(p.actual_amount) > 0).map((p) => ({ debt_id: p.debt_id, actual_amount: numv(p.actual_amount) }));
    setBusy(true);
    try {
      const sp = await journeyApi.confirmSprint(sprint.id, pays);
      track("sprint_confirmed", { outcome: "debt" });
      setSprint(sp);
      const confirmed = sp.confirmed_payments || [];
      const eliminatedNames = confirmed.filter((p) => p.eliminated).map((p) => p.name);
      const anyEliminated = eliminatedNames.length > 0;
      let allGone = false, nextTarget = null;
      try {
        const st = await journeyApi.sprintState();
        allGone = (st.debts || []).length > 0 && !st.current_target;
        nextTarget = st.current_target?.name || null;
      } catch {}
      setDoneInfo({ anyEliminated, eliminatedNames, allGone, nextTarget, totalAttacked: sp.total_attacked || 0, unassigned: sp.unassigned || 0 });
      setStep("done");
      celebrate(allGone ? "all_debt_gone" : anyEliminated ? "debt_eliminated" : "debt");
    } catch (e) { toast.error(apiError(e, "Could not confirm your payment.")); }
    finally { setBusy(false); }
  };

  const doConfirmBuild = async () => {
    const amt = numv(buildAmount);
    if (amt <= 0) return toast.error("Enter the amount you contributed.");
    setBusy(true);
    try {
      const sp = await journeyApi.confirmBuild(sprint.id, amt);
      setSprint(sp);
      setStep("done-build");
      celebrate(sp.goal_completed ? "goal_complete" : "goal");
    } catch (e) { toast.error(apiError(e, "Could not confirm your contribution.")); }
    finally { setBusy(false); }
  };

  const toDashboard = () => navigate("/dashboard");
  const goBack = () => {
    if (step === "adjust") setStep("entry");
    else if (step === "reveal") setStep("adjust");
    else if (step === "confirm" || step === "confirm-build") setStep("reveal");
    else navigate("/dashboard");
  };
  const backLabel = step === "entry" || step === "loading" ? "Back to dashboard" : "Back";

  if (step === "loading") {
    return <DashboardShell><div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-[#E5E0D9] border-t-[#2F6B57]" /></div></DashboardShell>;
  }

  return (
    <DashboardShell>
      {step !== "done" && step !== "protected" && step !== "done-build" && step !== "whatif" && (
        <button onClick={goBack} data-testid="money-landed-back" className="mb-4 inline-flex items-center gap-1.5 text-sm text-[#70584A] hover:text-[#164C3B]">
          <ArrowLeft className="h-4 w-4" /> {backLabel}
        </button>
      )}

      {step === "entry" && (
        <div data-testid="ml-entry">
          <PageHead title="Feeling rich?" sub="However it arrived, let's work out your next move. Nothing changes until you confirm a payment." />
          <div className="mb-4 rounded-2xl border border-[#DDE7DF] bg-[#F3F7F4] p-4" data-testid="ml-income-inclusive">
            <p className="text-sm text-[#164C3B]">
              Paycheck, hourly wages, commission, freelance or contract payment, a bonus, gig or side-hustle money,
              business income or a draw, a refund, a gift, or any other income. If it is money you can actually use, it counts.
            </p>
            <p className="mt-2 text-xs text-[#70584A]">Enter only what is genuinely available to put to work. A reimbursement or a refund you already owe elsewhere is not money to move.</p>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); if (numv(actual) <= 0) { toast.error("Enter the amount that actually landed."); return; } setStep("adjust"); }} className="rounded-2xl pe-surface p-6">
            {!isVariable && expected > 0 && (
              <p className="mb-4 rounded-xl bg-[#F5F2EC] p-3 text-sm text-[#70584A]">
                Your expected take-home is <span className="font-semibold text-[#164C3B]">{fmt(expected)}</span>. Is that what actually landed? Confirm or enter the real amount.
              </p>
            )}
            <label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]">Amount that actually landed</label>
            {money(actual, setActual, "ml-actual-input")}
            <p className="mt-2 text-xs text-[#A89A88]">This actual amount is the source of truth for this event.</p>
            <label className="mt-4 block text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]">Date it landed</label>
            <input type="date" value={landedDate} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setLandedDate(e.target.value)} data-testid="ml-landed-date" className={input} />
            <p className="mt-2 text-xs text-[#A89A88]">We reserve only the obligations due between this date and your next expected paycheck.</p>
            <Button type="submit" data-testid="ml-entry-next" disabled={busy} className="mt-5 min-h-12 w-full rounded-xl bg-[#2F6B57] font-semibold text-white hover:bg-[#255643]">
              Continue <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </form>
          <button type="button" data-testid="ml-open-whatif" onClick={() => { setWhatIfFromHome(false); setWhatIfAmount(actual || ""); setStep("whatif"); }}
            className="mx-auto mt-5 flex items-center gap-1.5 text-sm font-medium text-[#2F6B57] hover:underline">
            <Sparkles className="h-4 w-4" /> Just curious? Try a What If scenario
          </button>
        </div>
      )}

      {step === "whatif" && (
        <WhatIf
          amount={whatIfAmount} setAmount={setWhatIfAmount} plan={whatIfPlan}
          onRun={() => runWhatIf(whatIfAmount)} onMakeMove={makeThisMyMove}
          onNeverMind={neverMindWhatIf} onReset={() => setWhatIfPlan(null)} busy={busy}
        />
      )}

      {step === "adjust" && (
        <div data-testid="ml-adjust">
          <PageHead title="Anything different this time?" sub="Your Foundation gives us the usual numbers. If nothing changed, we'll use them. If life happened, we'll update the move. The software calculates. You decide." />

          {hasAdjustment() && (
            <div className="mb-4 rounded-2xl border border-[#C9A86A]/60 bg-[#FBF7EF] p-4" data-testid="ml-adjust-applied">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#9A7B3A]">Applied to this move</p>
              <p className="mt-1 text-sm text-[#164C3B]">{adjustmentLabel()} <span className="text-[#70584A]">· this event only</span></p>
              <button type="button" data-testid="ml-adjust-clear" onClick={clearAdjustment} className="mt-1 text-xs font-medium text-[#70584A] underline hover:text-[#164C3B]">Remove</button>
            </div>
          )}

          <div className="space-y-3">
            <button type="button" data-testid="ml-change-none" disabled={busy} onClick={() => { clearAdjustment(); doCalculate(); }}
              className="flex w-full items-center justify-between rounded-2xl border-2 border-[#2F6B57] bg-[#DDE7DF] px-5 py-4 text-left transition hover:bg-[#d2e0d6]">
              <div>
                <p className="font-serif text-lg text-[#164C3B]">Everything looks right</p>
                <p className="text-sm text-[#2F6B57]">Use my Foundation and show my Edit.</p>
              </div>
              <ArrowRight className="h-5 w-5 shrink-0 text-[#164C3B]" />
            </button>
            <button type="button" data-testid="ml-change-life" disabled={busy} onClick={() => navigate("/dashboard/life-happened")}
              className="flex w-full items-center justify-between rounded-2xl border-2 border-[#E5E0D9] bg-white px-5 py-4 text-left transition hover:border-[#C9A86A] hover:bg-[#FBF7EF]">
              <div>
                <p className="font-serif text-lg text-[#171717]">Yep, life happened</p>
                <p className="text-sm text-[#70584A]">Something changed. Update the move first.</p>
              </div>
              <ArrowRight className="h-5 w-5 shrink-0 text-[#C9A86A]" />
            </button>
          </div>

          {hasAdjustment() && (
            <Button data-testid="ml-calculate" disabled={busy} onClick={doCalculate} className="mt-4 min-h-12 w-full rounded-xl bg-[#2F6B57] font-semibold text-white hover:bg-[#255643]">
              {busy ? "Calculating…" : "Show my Edit with this change"} <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          )}

          <div className="mt-6">
            <Button variant="ghost" onClick={() => setStep("entry")} className="min-h-11 rounded-xl text-[#70584A] hover:bg-[#F5F2EC]"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
          </div>
        </div>
      )}

      {step === "reveal" && sprint && <Reveal sprint={sprint} onAccept={doAccept} onProtect={doProtect} onProtectOptIn={doProtectOptIn} onSprintAnyway={() => setShowOverride(true)} onChooseGoal={() => navigate("/dashboard/goal")} onKeepUnassigned={doKeepUnassigned} onDashboard={() => navigate("/dashboard")} onMoreMoney={moreMoneyShowedUp} busy={busy} />}

      {showOverride && sprint && (
        <OverrideModal sprint={sprint} busy={busy} onConfirm={doOverride} onCancel={() => setShowOverride(false)} />
      )}

      {step === "protected" && sprint && <ProtectedDone sprint={sprint} onDashboard={() => navigate("/dashboard")} onProgress={() => navigate("/dashboard/progress")} />}

      {step === "confirm-build" && sprint && (
        <div data-testid="ml-confirm-build">
          <PageHead title="Did you make the contribution?" sub={`Confirm only after you've completed this contribution toward ${sprint.goal_name || "your goal"} outside The Paycheck Edit. Nothing counts until you confirm.`} />
          <div className="rounded-2xl pe-surface p-6">
            <label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]">Amount contributed</label>
            {money(buildAmount, setBuildAmount, "ml-build-amount")}
            <p className="mt-2 text-xs text-[#A89A88]">Proposed: {fmt(sprint.build_recommended || 0)}. You decide the real amount.</p>
            <Button data-testid="ml-build-confirm" disabled={busy} onClick={doConfirmBuild} className="mt-5 min-h-12 w-full rounded-xl bg-[#2F6B57] font-semibold text-white hover:bg-[#255643]">I MADE THIS MONEY MOVE</Button>
            <Button variant="ghost" disabled={busy} onClick={toDashboard} className="mt-2 min-h-11 w-full rounded-xl text-[#70584A] hover:bg-[#F5F2EC]">Not yet</Button>
          </div>
        </div>
      )}

      {step === "done-build" && sprint && (
        sprint.goal_completed && sprint.completed_goal ? (
          <div className="text-center" data-testid="ml-done-build">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#DDE7DF] text-[#164C3B]"><CheckCircle2 className="h-8 w-8" /></span>
            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-[#9A7B3A]" data-testid="goal-complete-headline">{(sprint.completed_goal.label || sprint.completed_goal.name || "Goal").toUpperCase()}.</p>
            <h1 className="mt-2 font-serif text-4xl text-[#164C3B]">You did that.</h1>
            <p className="mt-3 text-[#2F6B57]">You put {fmt(sprint.completed_goal.total_contributed || 0)} toward this goal across {sprint.completed_goal.completed_count} {sprint.completed_goal.completed_count === 1 ? "Edit" : "Edits"}.</p>
            <p className="mt-1 font-medium text-[#164C3B]">{sprint.completed_goal.name} goal complete. Your confirmed contributions are preserved in The Receipts.</p>
            {(sprint.unassigned || 0) > 0 ? (
              <div className="mx-auto mt-5 max-w-sm rounded-2xl border border-[#C9A86A]/50 bg-[#FBF7EF] p-5" data-testid="ml-build-surplus-done">
                <p className="font-serif text-2xl text-[#164C3B]" data-testid="ml-surplus-amount">{fmt(sprint.unassigned)} still needs a job</p>
                <p className="mt-1 text-sm text-[#70584A]">Where do you want it to go? Nothing was moved onto another goal. You choose next.</p>
              </div>
            ) : (
              <p className="mt-5 text-[#525252]">Your money needs its next job.</p>
            )}
            <div className="mx-auto mt-4 flex max-w-sm flex-col gap-3 sm:flex-row sm:justify-center">
              {(sprint.unassigned || 0) > 0 ? (
                <>
                  <Button data-testid="ml-choose-next-job" onClick={() => navigate("/dashboard/assign")} className="min-h-12 w-full rounded-xl bg-[#2F6B57] px-7 font-semibold text-white hover:bg-[#255643] sm:w-auto">CHOOSE ITS NEXT JOB</Button>
                  <Button variant="outline" data-testid="ml-keep-unassigned" onClick={() => navigate("/dashboard")} className="min-h-12 w-full rounded-xl border-[#E5E0D9] px-6 sm:w-auto">KEEP IT UNASSIGNED FOR NOW</Button>
                </>
              ) : (
                <Button data-testid="ml-choose-next-goal" onClick={() => navigate("/dashboard/goal")} className="min-h-12 w-full rounded-xl bg-[#2F6B57] px-7 font-semibold text-white hover:bg-[#255643] sm:w-auto">CHOOSE MY NEXT GOAL</Button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center" data-testid="ml-done-build">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#FBF7EF] text-[#C9A86A]"><CheckCircle2 className="h-8 w-8" /></span>
            <h1 className="mt-5 font-serif text-3xl text-[#171717]">Edit confirmed.</h1>
            <p className="mt-2 text-[#525252]">You recorded {fmt(sprint.goal_confirmed || 0)} toward {sprint.goal_name || "your goal"}. This confirmed action is now in The Receipts.</p>
            <div className="mx-auto mt-6 flex max-w-sm flex-col gap-3 sm:flex-row sm:justify-center">
              <Button onClick={() => navigate("/dashboard")} className="min-h-11 w-full rounded-xl bg-[#2F6B57] px-6 font-semibold text-white hover:bg-[#255643] sm:w-auto">Back to dashboard</Button>
              <Button variant="outline" onClick={() => navigate("/dashboard/goal")} className="min-h-11 w-full rounded-xl border-[#E5E0D9] px-6 sm:w-auto">See my goal</Button>
            </div>
          </div>
        )
      )}

      {step === "confirm" && sprint && (
        <Confirm sprint={sprint} payments={payments} setPayments={setPayments} onConfirm={doConfirm} onProtect={doProtect} onLater={toDashboard} onChangeMove={doChangeMove} busy={busy} />
      )}

      {step === "done" && sprint && (
        doneInfo?.allGone ? (
          // ELIMINATE complete: the final eligible consumer debt is gone. Major
          // lifecycle event -> BUILD unlocked. Any excess stays unassigned until
          // the customer chooses their next goal (never auto-assigned).
          <div className="text-center" data-testid="ml-done">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#DDE7DF] text-[#164C3B]"><CheckCircle2 className="h-8 w-8" /></span>
            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-[#164C3B]" data-testid="done-eliminate-complete">You did it.</p>
            <h1 className="mt-2 font-serif text-4xl text-[#164C3B]">$0 eligible debt remaining.</h1>
            <p className="mt-3 text-[#2F6B57]">You've completed Eliminate and unlocked Build.</p>
            <div className="mx-auto mt-5 max-w-sm rounded-2xl border border-[#C9A86A]/50 bg-[#FBF7EF] p-5">
              <p className="font-serif text-lg text-[#171717]">Your consumer debt is cleared.</p>
              <p className="mt-1 text-sm text-[#70584A]">Choose what you'd like to work toward next.</p>
              <p className="mt-3 text-xs font-semibold uppercase tracking-[0.16em] text-[#9A7B3A]">Build unlocked</p>
              {doneInfo.unassigned > 0 && <p className="mt-2 text-sm text-[#70584A]">{fmt(doneInfo.unassigned)} is left over and still needs a job. You choose where it goes.</p>}
            </div>
            <div className="mx-auto mt-6 flex max-w-sm flex-col gap-3 sm:flex-row sm:justify-center">
              {doneInfo.unassigned > 0 ? (
                <Button data-testid="ml-choose-next-job" onClick={() => navigate("/dashboard/assign")} className="min-h-12 w-full rounded-xl bg-[#2F6B57] px-7 font-semibold text-white hover:bg-[#255643] sm:w-auto">CHOOSE ITS NEXT JOB</Button>
              ) : (
                <Button data-testid="ml-choose-next-goal" onClick={() => navigate("/dashboard/goal")} className="min-h-12 w-full rounded-xl bg-[#2F6B57] px-7 font-semibold text-white hover:bg-[#255643] sm:w-auto">CHOOSE MY NEXT GOAL</Button>
              )}
              <Button variant="outline" onClick={() => navigate("/dashboard")} className="min-h-12 w-full rounded-xl border-[#E5E0D9] px-6 sm:w-auto">Back to dashboard</Button>
            </div>
          </div>
        ) : doneInfo?.anyEliminated ? (
          // An individual debt was eliminated and more eligible debt remains.
          <div className="text-center" data-testid="ml-done">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#DDE7DF] text-[#164C3B]"><CheckCircle2 className="h-8 w-8" /></span>
            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-[#164C3B]" data-testid="done-target-eliminated">Target eliminated.</p>
            <h1 className="mt-2 font-serif text-3xl text-[#164C3B]">{doneInfo.eliminatedNames.join(" and ")} {doneInfo.eliminatedNames.length > 1 ? "are" : "is"} gone.</h1>
            <p className="mt-3 text-[#2F6B57]">You attacked {fmt(doneInfo.totalAttacked)} this Edit. Balance now $0.</p>
            {doneInfo.nextTarget && <p className="mt-1 font-medium text-[#164C3B]">Next target: {doneInfo.nextTarget}.</p>}
            <div className="mx-auto mt-6 flex max-w-sm flex-col gap-3 sm:flex-row sm:justify-center">
              <Button onClick={() => navigate("/dashboard")} className="min-h-11 w-full rounded-xl bg-[#2F6B57] px-6 font-semibold text-white hover:bg-[#255643] sm:w-auto">Back to dashboard</Button>
              <Button variant="outline" onClick={() => navigate("/dashboard/progress")} className="min-h-11 w-full rounded-xl border-[#E5E0D9] px-6 sm:w-auto">See progress</Button>
            </div>
          </div>
        ) : (
          // Partial debt attack: target lowered but not eliminated.
          <div className="text-center" data-testid="ml-done">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#DDE7DF] text-[#164C3B]"><CheckCircle2 className="h-8 w-8" /></span>
            <h1 className="mt-5 font-serif text-3xl text-[#171717]">Edit confirmed.</h1>
            <p className="mt-2 text-[#525252]">You recorded {fmt(sprint.total_attacked || 0)} toward your debt. This confirmed action is now in The Receipts.</p>
            <div className="mx-auto mt-6 flex max-w-sm flex-col gap-3 sm:flex-row sm:justify-center">
              <Button onClick={() => navigate("/dashboard")} className="min-h-11 w-full rounded-xl bg-[#2F6B57] px-6 font-semibold text-white hover:bg-[#255643] sm:w-auto">Back to dashboard</Button>
              <Button variant="outline" onClick={() => navigate("/dashboard/progress")} className="min-h-11 w-full rounded-xl border-[#E5E0D9] px-6 sm:w-auto">See progress</Button>
            </div>
          </div>
        )
      )}
    </DashboardShell>
  );
}

function RunwayProgress({ before, after, target, added }) {
  const pct = target > 0 ? Math.min(Math.round((after / target) * 100), 100) : 0;
  const remaining = Math.max(target - after, 0);
  return (
    <div className="mt-5 rounded-2xl border border-[#C9A86A]/50 bg-[#FBF7EF] p-6" data-testid="ml-runway-card">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#9A7B3A]"><ShieldCheck className="h-4 w-4" /> Your Protected Runway</p>
      <p className="mt-2 font-serif text-3xl text-[#171717]" data-testid="ml-runway-beforeafter">{fmt(before)} <span className="text-[#C9A86A]">→</span> {fmt(after)}</p>
      <p className="mt-1 text-sm font-medium text-[#2F6B57]">+{fmt(added)} protected this time</p>
      <div className="mt-4 h-2.5 w-full overflow-hidden rounded-full bg-[#EADFC7]"><div className="h-full rounded-full bg-[#C9A86A]" style={{ width: `${pct}%` }} data-testid="ml-runway-bar" /></div>
      <div className="mt-2 flex items-center justify-between text-sm text-[#70584A]">
        <span>{fmt(after)} of {fmt(target)} protected</span>
        <span className="font-medium">{pct}%</span>
      </div>
      {remaining > 0 && <p className="mt-1 text-sm text-[#8A7C6B]">{fmt(remaining)} to go</p>}
    </div>
  );
}

function Reveal({ sprint, onAccept, onProtect, onProtectOptIn, onSprintAnyway, onChooseGoal, onKeepUnassigned, onDashboard, onMoreMoney, busy }) {
  const b = sprint.foundation_breakdown || {};
  const [showBreak, setShowBreak] = useState(false);
  const [showMath, setShowMath] = useState(false);
  const [showProtect, setShowProtect] = useState(false);
  const pd = b.protection_detail || {};
  const protAmt = b.protection || 0;
  const isBuild = sprint.sprint_type === "build";
  const isBuildUnassigned = sprint.sprint_type === "build_unassigned";
  const isProtectionSprint = protAmt > 0 && (sprint.potential_sprint_money || 0) === 0;
  const isSmallEdit = !isProtectionSprint && (sprint.potential_sprint_money || 0) > 0 && (sprint.potential_sprint_money || 0) <= SMALL_EDIT_MAX;
  const before = pd.existing_protected_runway || 0;
  const target = pd.protected_runway_target || 0;
  const Row = ({ label, value, sub, minus }) => (
    <div className="flex items-start justify-between py-1.5">
      <span className="text-[#70584A]">{label}{sub && <span className="block text-xs text-[#A89A88]">{sub}</span>}</span>
      <span className={`font-medium ${minus ? "text-[#8B3A2B]" : "text-[#171717]"}`}>{minus ? "− " : ""}{fmt(value)}</span>
    </div>
  );
  return (
    <div data-testid="ml-reveal">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#C9A86A]">{isProtectionSprint ? "Protection Edit" : "Your Edit is ready."}</p>
      <h1 className="mt-1 font-serif text-3xl text-[#171717] sm:text-4xl">{isProtectionSprint ? "Here's the move." : "Here's one way your money could work."}</h1>
      {!isProtectionSprint && (
        <p className="mt-2 text-sm leading-relaxed text-[#525252]" data-testid="ml-illustrative-note">
          Based on the numbers and priorities you entered, we've calculated an illustrative allocation for you to review.
        </p>
      )}

      {isProtectionSprint ? (
        <div className="mt-5 rounded-2xl border border-[#DDE7DF] bg-[#DDE7DF] p-6" data-testid="ml-protection-sprint">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#164C3B]">Protection Edit</p>
          <p className="mt-2 font-serif text-2xl text-[#164C3B]">No debt attack this time, and that's okay.</p>
          <p className="mt-1 text-sm text-[#2F6B57]">You're about to move {fmt(protAmt)} closer to your Protected Runway. Protecting real life comes first.</p>
        </div>
      ) : (
        <div className="mt-6 rounded-3xl bg-[#DDE7DF] p-7 ring-1 ring-[#2F6B57]/15 sm:p-8">
          {isSmallEdit && <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#9A7B3A]" data-testid="ml-small-edit">Small edit. Still counts.</p>}
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#164C3B]">Estimated available</p>
          <p className="mt-2 font-serif text-5xl leading-none text-[#164C3B] sm:text-6xl" data-testid="ml-potential">{fmt(sprint.potential_sprint_money)}</p>
          <p className="mt-2 text-sm text-[#2F6B57]">From {fmt(b.income_received)} landed, after {fmt(b.total_obligations)} reserved for real life{protAmt > 0 ? ` and ${fmt(protAmt)} protected` : ""}.</p>
          <p className="mt-2 text-xs leading-relaxed text-[#70584A]">This estimate uses the information you entered. Check your actual balances and upcoming expenses before acting.</p>
        </div>
      )}

      {protAmt > 0 && <RunwayProgress before={before} after={before + protAmt} target={target} added={protAmt} />}

      {/* Why this move? Deterministic explanation + progressive "See the math". */}
      <div className="mt-4 rounded-3xl bg-white ring-1 ring-[#E5E0D9] shadow-[0_1px_3px_rgba(22,76,59,0.04)]">
        <button type="button" onClick={() => setShowBreak((v) => !v)} data-testid="ml-breakdown-toggle" aria-expanded={showBreak} className="flex w-full items-center justify-between px-5 py-4 text-left text-sm font-medium text-[#164C3B]">
          Why this move?
          <ChevronDown className={`h-4 w-4 transition-transform ${showBreak ? "rotate-180" : ""}`} />
        </button>
        {showBreak && (
          <div className="border-t border-[#F0EBE2] px-5 py-4" data-testid="ml-breakdown">
            <p className="text-sm leading-relaxed text-[#525252]" data-testid="ml-why-move">{whyThisMove(sprint)}</p>
            <button type="button" onClick={() => setShowMath((v) => !v)} data-testid="ml-see-math" aria-expanded={showMath}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-[#2F6B57] hover:underline">
              See the math <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showMath ? "rotate-180" : ""}`} />
            </button>
            {showMath && (
              <div className="mt-3 border-t border-[#F0EBE2] pt-3 text-sm" data-testid="ml-math">
                <Row label="Base income" value={b.base_income} />
                {b.additional_income > 0 && <Row label="Additional income (this event)" value={b.additional_income} />}
                <Row label="Obligations due this cycle" value={b.period_essentials} sub={(b.essentials_detail || []).map((e) => e.name).join(", ") || "None due in window"} minus />
                <Row label="Living Bucket reserved" value={b.living_bucket} sub={b.living_bucket === 0 ? "Already reserved earlier this cycle" : "Everyday spending for this cycle"} minus />
                <Row label="Required debt minimums" value={b.period_debt_minimums} sub={(b.debt_detail || []).map((d) => d.name).join(", ") || "None due in window"} minus />
                {protAmt > 0 && <Row label="Protected Runway allocation" value={protAmt} sub={target ? `Toward your ${fmt(target)} runway target` : null} minus />}
                <div className="mt-2 flex items-center justify-between border-t border-[#E5E0D9] pt-3">
                  <span className="font-semibold text-[#164C3B]">Estimated available</span>
                  <span className="font-semibold text-[#164C3B]">{fmt(sprint.potential_sprint_money)}</span>
                </div>
                {(sprint.unassigned || 0) > 0 && (
                  <div className="mt-2 flex items-center justify-between text-xs text-[#70584A]" data-testid="ml-math-unassigned">
                    <span>Still unassigned (looking for a job)</span>
                    <span className="font-medium">{fmt(sprint.unassigned)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {sprint.allocations.length > 0 ? (
        <div className="mt-5 space-y-3" data-testid="ml-allocations">
          <div data-testid="ml-snowball-head">
            <p className="pe-eyebrow">Your debt payoff calculation</p>
            <p className="mt-1.5 text-sm leading-relaxed text-[#70584A]">
              Using the balances you entered, this illustration starts with your smallest eligible debt while accounting for the minimum payments you've entered for your other debts. Check your lender balance and due dates before making a payment.
            </p>
          </div>
          {sprint.allocations.map((a) => {
            const pct = a.current_balance > 0 ? Math.round(((a.current_balance - a.projected_balance) / a.current_balance) * 100) : 0;
            return (
              <div key={a.debt_id} className="rounded-3xl bg-white p-6 ring-1 ring-[#E5E0D9]/80 shadow-[0_1px_3px_rgba(22,76,59,0.04)]">
                <div className="flex items-center justify-between">
                  <p className="font-serif text-xl text-[#171717]">{a.name}</p>
                  {a.eliminated && <span className="rounded-full bg-[#DDE7DF] px-3 py-1 text-xs font-semibold text-[#164C3B]">Eliminated</span>}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-y-2 text-sm sm:grid-cols-4">
                  <div><p className="text-[#A89A88]">Current</p><p className="font-medium text-[#171717]">{fmt(a.current_balance)}</p></div>
                  <div><p className="text-[#A89A88]">Illustrative</p><p className="font-medium text-[#164C3B]">{fmt(a.recommended)}</p></div>
                  <div><p className="text-[#A89A88]">Projected</p><p className="font-medium text-[#171717]">{fmt(a.projected_balance)}</p></div>
                  <div><p className="text-[#A89A88]">Eliminated</p><p className="font-medium text-[#171717]">{pct}%</p></div>
                </div>
                <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-[#F0EBE2]"><div className="h-full rounded-full bg-[#2F6B57]" style={{ width: `${pct}%` }} /></div>
              </div>
            );
          })}
          {sprint.allocations.length > 1 && (
            <p className="flex items-center gap-1.5 text-sm text-[#70584A]"><TrendingDown className="h-4 w-4" /> Money rolls to the next smallest debt automatically.</p>
          )}
        </div>
      ) : isBuild ? (
        <div className="mt-5 rounded-2xl border border-[#ECE6DC] bg-[#FCFBF8] p-6" data-testid="ml-build-move">
          <p className="pe-eyebrow">Your proposed move · Build</p>
          <p className="mt-1.5 font-serif text-2xl text-[#164C3B]">{fmt(sprint.build_recommended || 0)} toward {sprint.goal_name || "your goal"}</p>
          <p className="mt-1 text-sm text-[#70584A]">Debt is clear and real life is protected. This money can build your next goal.</p>
          {(sprint.unassigned || 0) > 0 && (
            <div className="mt-4 rounded-xl border border-[#2F6B57]/30 bg-[#DDE7DF] p-4" data-testid="ml-build-surplus">
              <p className="text-sm font-medium text-[#164C3B]">That completes this goal.</p>
              <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#2F6B57]">Money still available</p>
              <p className="font-serif text-2xl text-[#164C3B]" data-testid="ml-build-surplus-amount">{fmt(sprint.unassigned)} is still looking for a job</p>
              <p className="mt-1 text-sm text-[#2F6B57]">Nice problem to have. Your goal is covered and you have money left over. It stays unassigned until you choose where it goes. Keeping it unassigned is a perfectly good choice.</p>
            </div>
          )}
        </div>
      ) : isBuildUnassigned ? (
        <div className="mt-5 rounded-2xl border border-[#DDE7DF] bg-[#DDE7DF] p-6 text-center" data-testid="ml-build-unassigned">
          <p className="font-serif text-xl text-[#164C3B]">You ran out of debt.</p>
          <p className="mt-2 font-serif text-2xl text-[#164C3B]" data-testid="ml-unassigned-amount">{fmt(sprint.potential_sprint_money || 0)} is still looking for a job</p>
          <p className="mt-1 text-sm text-[#2F6B57]">No debt left to attack. Choose a BUILD goal to give this money a job, or keep it unassigned for now. Keeping it unassigned is a perfectly good choice.</p>
        </div>
      ) : !isProtectionSprint ? (
        <div className="mt-5 rounded-2xl border border-[#DDE7DF] bg-[#DDE7DF] p-6 text-center" data-testid="ml-no-sprint">
          <p className="font-serif text-2xl text-[#164C3B]" data-testid="ml-youre-good">You're good for now.</p>
          <p className="mt-2 text-sm text-[#2F6B57]">Everything that needed covering is covered. There is no move to make from this money, and that is a win, not a miss. If more money shows up, you can start a fresh Edit any time.</p>
          <div className="mx-auto mt-5 flex max-w-sm flex-col gap-3 sm:flex-row sm:justify-center">
            <Button data-testid="ml-more-money" onClick={onMoreMoney} className="min-h-11 w-full rounded-xl bg-[#2F6B57] px-6 font-semibold text-white hover:bg-[#255643] sm:w-auto">MORE MONEY SHOWED UP</Button>
            <Button data-testid="ml-youre-good-done" variant="outline" onClick={onDashboard} className="min-h-11 w-full rounded-xl border-[#E5E0D9] px-6 sm:w-auto">BACK TO DASHBOARD</Button>
          </div>
        </div>
      ) : null}

      {pd.income_type === "predictable" && pd.can_protect && protAmt === 0 && (
        <div className="mt-4 rounded-2xl border border-[#C9A86A]/50 bg-[#FBF7EF] p-4" data-testid="ml-protect-optin-card">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#9A7B3A]"><ShieldCheck className="h-4 w-4" /> Protect first?</p>
          <p className="mt-1.5 text-sm text-[#70584A]">Your Protected Runway has a <span className="font-semibold text-[#164C3B]">{fmt(pd.protection_gap || 0)}</span> gap. Stable income means this is optional, but you can build it with this money instead.</p>
          <Button data-testid="ml-protect-optin" variant="outline" disabled={busy} onClick={onProtectOptIn}
            className="mt-3 min-h-11 w-full rounded-xl border-[#2F6B57] text-sm font-semibold text-[#164C3B] hover:bg-[#F5F2EC]">
            <ShieldCheck className="mr-1.5 h-4 w-4" /> USE THIS EDIT FOR PROTECTION INSTEAD
          </Button>
        </div>
      )}

      <div className="mt-4 space-y-3">
        {protAmt > 0 && sprint.allocations.length === 0 ? (
          <>
            <Button data-testid="ml-keep-protected" disabled={busy} onClick={onProtect} className="min-h-12 w-full rounded-xl bg-[#2F6B57] text-base font-semibold tracking-wide text-white hover:bg-[#255643]"><ShieldCheck className="mr-1.5 h-4 w-4" /> BUILD MY PROTECTED RUNWAY</Button>
            {isBuild && (sprint.unassigned || 0) > 0 && <p className="text-center text-xs text-[#8A7C6B]">Confirming adds {fmt(protAmt)} to your Protected Runway. {fmt(sprint.unassigned)} stays unassigned until you choose.</p>}
            <button type="button" data-testid="ml-sprint-anyway" disabled={busy} onClick={onSprintAnyway} className="mx-auto block text-sm font-medium text-[#8A7C6B] underline decoration-[#D6CBB8] underline-offset-4 hover:text-[#70584A]">
              I still want to move some of this money
            </button>
          </>
        ) : isBuild ? (
          <Button data-testid="ml-ready-build" disabled={busy} onClick={onAccept} className="min-h-[3.25rem] w-full rounded-full bg-[#2F6B57] px-10 text-base font-semibold tracking-wide text-white shadow-[0_16px_38px_-18px_rgba(22,76,59,0.5)] transition-colors hover:bg-[#255643] sm:w-auto sm:min-w-[18rem]">I'M READY TO EDIT</Button>
        ) : isBuildUnassigned ? (
          <>
            <Button data-testid="ml-choose-goal" disabled={busy} onClick={onChooseGoal} className="min-h-12 w-full rounded-xl bg-[#2F6B57] text-base font-semibold tracking-wide text-white hover:bg-[#255643]">CHOOSE MY BUILD GOAL</Button>
            <Button data-testid="ml-keep-unassigned" variant="outline" disabled={busy} onClick={onKeepUnassigned} className="min-h-12 w-full rounded-xl border-[#E5E0D9] text-base font-semibold text-[#164C3B] hover:bg-[#F5F2EC]">KEEP IT UNASSIGNED FOR NOW</Button>
          </>
        ) : (
        <>
        {sprint.allocations.length > 0 && (
          <Button data-testid="ml-ready-sprint" disabled={busy} onClick={onAccept} className="min-h-[3.25rem] w-full rounded-full bg-[#164C3B] px-10 text-base font-semibold tracking-wide text-white shadow-[0_16px_38px_-18px_rgba(22,76,59,0.55)] transition-colors hover:bg-[#0f3528]">MAKE THIS EDIT</Button>
        )}
        {protAmt > 0 ? (
          <div className="group relative flex items-center gap-2">
            <Button data-testid="ml-keep-protected" variant="outline" disabled={busy} onClick={onProtect} className="min-h-12 flex-1 rounded-xl border-[#2F6B57] bg-[#2F6B57] text-base font-semibold text-white hover:bg-[#255643]"><ShieldCheck className="mr-1.5 h-4 w-4" /> KEEP IT PROTECTED</Button>
            <button
              type="button"
              data-testid="ml-protect-info-toggle"
              aria-label="What does Keep It Protected mean?"
              aria-expanded={showProtect}
              onClick={() => setShowProtect((v) => !v)}
              className="peer flex min-h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#E5E0D9] text-[#70584A] hover:text-[#164C3B] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2F6B57]/40"
            >
              <Info className="h-5 w-5" />
            </button>
            <div
              role="tooltip"
              data-testid="ml-protect-info"
              className={`absolute left-0 top-full z-10 mt-2 w-full rounded-xl pe-surface p-3 text-sm leading-relaxed text-[#70584A] shadow-sm peer-hover:block peer-focus:block ${showProtect ? "block" : "hidden"}`}
            >
              Choosing this moves this money into your Protected Runway. The Paycheck Edit does not hold, transfer, or move your money. Your cash stays exactly where it is.
            </div>
          </div>
        ) : target > 0 ? (
          <div className="text-center" data-testid="ml-decline-row">
            <button
              type="button"
              data-testid="ml-keep-protected"
              disabled={busy}
              onClick={onProtect}
              className="mx-auto inline-flex min-h-11 items-center justify-center rounded-xl px-6 text-sm font-medium text-[#70584A] underline decoration-[#E5E0D9] underline-offset-4 transition-colors hover:text-[#164C3B] disabled:opacity-60"
            >
              NOT RIGHT NOW
            </button>
            <p className="mx-auto mt-2 max-w-md text-xs leading-relaxed text-[#8A7C6B]">
              You decide not to move this money toward your debt right now. The Paycheck Edit does not hold, transfer, or move your money — your cash stays exactly where it is.
            </p>
          </div>
        ) : null}
        {protAmt > 0 && (
          <button type="button" data-testid="ml-sprint-anyway" disabled={busy} onClick={onSprintAnyway} className="mx-auto block text-sm font-medium text-[#8A7C6B] underline decoration-[#D6CBB8] underline-offset-4 hover:text-[#70584A]">
            I still want to move some of this money
          </button>
        )}
        </>
        )}
      </div>

      <p className="mt-6 text-center text-sm italic text-[#8A7C6B]">The Paycheck Edit shows you the move. You make the decision. We never move money or pay lenders for you.</p>
    </div>
  );
}

function OverrideModal({ sprint, busy, onConfirm, onCancel }) {
  const b = sprint.foundation_breakdown || {};
  const pd = b.protection_detail || {};
  const current = pd.existing_protected_runway || 0;
  const target = pd.protected_runway_target || 0;
  const gap = pd.protection_gap || 0;
  const redirect = b.protection || 0;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center" data-testid="ml-override-modal" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <p className="font-serif text-2xl text-[#171717]">Moving protected money</p>
        <p className="mt-3 text-sm leading-relaxed text-[#525252]">
          The Paycheck Edit recommends protecting this money because your Protected Runway isn't fully funded yet.
          Moving it may leave you with less cushion if income slows or an unexpected expense hits.
        </p>
        <div className="mt-4 space-y-1.5 rounded-xl bg-[#F5F2EC] p-4 text-sm" data-testid="ml-override-breakdown">
          <div className="flex justify-between"><span className="text-[#70584A]">Current protected amount</span><span className="font-medium text-[#171717]">{fmt(current)}</span></div>
          <div className="flex justify-between"><span className="text-[#70584A]">Recommended protected amount</span><span className="font-medium text-[#171717]">{fmt(target)}</span></div>
          <div className="flex justify-between"><span className="text-[#70584A]">Protection gap</span><span className="font-medium text-[#171717]">{fmt(gap)}</span></div>
          <div className="flex justify-between"><span className="text-[#70584A]">Money available from this event</span><span className="font-medium text-[#171717]" data-testid="ml-override-available">{fmt(redirect)}</span></div>
          <div className="flex justify-between border-t border-[#E5E0D9] pt-1.5"><span className="text-[#70584A]">You choose to move</span><span className="font-semibold text-[#8B3A2B]" data-testid="ml-override-amount">{fmt(redirect)}</span></div>
          <div className="flex justify-between"><span className="text-[#70584A]">Protection remaining after override</span><span className="font-medium text-[#171717]" data-testid="ml-override-remaining">{fmt(current)}</span></div>
        </div>
        <div className="mt-5 space-y-3">
          <Button data-testid="ml-override-confirm" disabled={busy} onClick={onConfirm} className="min-h-11 w-full rounded-xl bg-[#8B3A2B] font-semibold text-white hover:bg-[#75301f]">I understand, show me the Edit</Button>
          <Button data-testid="ml-override-cancel" variant="ghost" disabled={busy} onClick={onCancel} className="min-h-11 w-full rounded-xl text-[#164C3B] hover:bg-[#F5F2EC]">Never mind, keep it protected</Button>
        </div>
      </div>
    </div>
  );
}

function ProtectedDone({ sprint, onDashboard, onProgress }) {
  const after = sprint.protection_after || 0;
  const target = sprint.protection_target || 0;
  const added = sprint.protection_confirmed || 0;
  const pct = sprint.protection_percent_after || (target > 0 ? Math.round((after / target) * 100) : 0);
  if (added <= 0) {
    return (
      <div className="text-center" data-testid="ml-protected-done">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#DDE7DF] text-[#164C3B]"><ShieldCheck className="h-8 w-8" /></span>
        <h1 className="mt-5 font-serif text-3xl text-[#171717]">Kept protected.</h1>
        <p className="mt-2 text-[#525252]">You chose not to deploy this money right now. Nothing moved. Your cash stays exactly where it is.</p>
        <ProtectionCheckinControl sprintId={sprint.id} />
        <div className="mt-6 flex justify-center gap-3">
          <Button onClick={onDashboard} className="min-h-11 rounded-xl bg-[#2F6B57] px-6 font-semibold text-white hover:bg-[#255643]">Back to dashboard</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="text-center" data-testid="ml-protected-done">
      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#FBF7EF] text-[#C9A86A]"><ShieldCheck className="h-8 w-8" /></span>
      <h1 className="mt-5 font-serif text-3xl text-[#171717]">Protection Edit complete.</h1>
      <p className="mt-2 text-[#525252]">You just moved {fmt(added)} closer to your Protected Runway. Protection is progress, too.</p>
      <div className="mx-auto mt-6 max-w-sm rounded-2xl border border-[#C9A86A]/50 bg-[#FBF7EF] p-5 text-left">
        <p className="font-serif text-2xl text-[#171717]">{fmt(sprint.protection_before || 0)} <span className="text-[#C9A86A]">→</span> {fmt(after)}</p>
        <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-[#EADFC7]"><div className="h-full rounded-full bg-[#C9A86A]" style={{ width: `${Math.min(pct, 100)}%` }} /></div>
        <p className="mt-2 text-sm text-[#70584A]">{fmt(after)} of {fmt(target)} protected · {pct}%</p>
      </div>
      <ProtectionCheckinControl sprintId={sprint.id} />
      <div className="mx-auto mt-6 flex max-w-sm flex-col gap-3 sm:flex-row sm:justify-center">
        <Button onClick={onDashboard} className="min-h-11 w-full rounded-xl bg-[#2F6B57] px-6 font-semibold text-white hover:bg-[#255643] sm:w-auto">Back to dashboard</Button>
        <Button variant="outline" onClick={onProgress} className="min-h-11 w-full rounded-xl border-[#E5E0D9] px-6 sm:w-auto">See progress</Button>
      </div>
    </div>
  );
}

// Contextual re-engagement control shown where the customer intentionally keeps
// money protected. Schedules an operational Protection check-in (one active per
// sprint). No reminder creates no job.
function ProtectionCheckinControl({ sprintId }) {
  const [scheduled, setScheduled] = useState(null);
  const [declined, setDeclined] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pickDate, setPickDate] = useState("");
  const tz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "UTC"; } })();
  const schedule = async (opts) => {
    setBusy(true);
    try {
      const c = await journeyApi.createCheckin({ type: "protection", related_entity_id: sprintId, timezone: tz, ...opts });
      setScheduled(c.local_display);
      toast.success(`Got it. We will check back ${c.local_display}.`);
    } catch { toast.error("Could not schedule that check-in."); }
    finally { setBusy(false); }
  };
  const declineReminder = async () => {
    setBusy(true);
    try {
      await journeyApi.declineCheckin({ type: "protection", related_entity_id: sprintId });
      setDeclined(true);
      toast.success("Got it. No reminder scheduled.");
    } catch { toast.error("Could not update your reminder."); }
    finally { setBusy(false); }
  };
  if (declined) {
    return <p className="mx-auto mt-6 max-w-sm rounded-xl bg-[#F5F2EC] p-3 text-sm text-[#70584A]" data-testid="protection-checkin-declined">Got it. No reminder scheduled. You can set one anytime from Profile.</p>;
  }
  if (scheduled) {
    return <p className="mx-auto mt-6 max-w-sm rounded-xl bg-[#DDE7DF] p-3 text-sm text-[#164C3B]" data-testid="protection-checkin-set">Got it. We will check back {scheduled}. You can change it in Profile.</p>;
  }
  return (
    <div className="mx-auto mt-6 max-w-sm rounded-2xl pe-surface p-5 text-left" data-testid="protection-checkin-control">
      <p className="font-medium text-[#171717]">Not ready to make this Edit yet?</p>
      <p className="mt-1 text-sm text-[#70584A]">No problem. When should The Paycheck Edit check back in?</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {[10, 15, 30].map((d) => (
          <button key={d} type="button" onClick={() => schedule({ in_days: d })} data-testid={`protection-checkin-${d}`} className="min-h-10 rounded-full border border-[#2F6B57]/40 px-4 text-sm font-medium text-[#164C3B] hover:bg-[#F5F2EC]">{d} days</button>
        ))}
      </div>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <label className="sr-only" htmlFor="protection-pick-date">Pick a check-in date</label>
        <input id="protection-pick-date" type="date" value={pickDate} onChange={(e) => setPickDate(e.target.value)} data-testid="protection-checkin-pickdate" className="h-10 rounded-xl pe-surface px-3 text-[#171717] focus:border-[#2F6B57] focus:outline-none" />
        <Button disabled={!pickDate} onClick={() => schedule({ date: pickDate })} data-testid="protection-checkin-pick-save" className="min-h-10 rounded-xl bg-[#2F6B57] px-4 text-sm font-semibold text-white hover:bg-[#255643]">Set date</Button>
      </div>
      <button type="button" disabled={busy} onClick={declineReminder} data-testid="protection-checkin-none" className="mt-3 text-sm font-medium text-[#70584A] hover:underline disabled:opacity-50">No reminder</button>
    </div>
  );
}


function Confirm({ sprint, payments, setPayments, onConfirm, onProtect, onLater, onChangeMove, busy }) {
  const [editing, setEditing] = useState(false);
  const total = useMemo(() => payments.reduce((s, p) => s + (parseFloat(p.actual_amount) || 0), 0), [payments]);
  const setAmt = (id, v) => setPayments(payments.map((p) => (p.debt_id === id ? { ...p, actual_amount: v.replace(/[^0-9.]/g, "") } : p)));

  return (
    <div data-testid="ml-confirm">
      <PageHead title="Make the payment" sub="You elected to pay the amounts below. Make the payment directly to your lender, then confirm here." />
      <div className="space-y-3">
        {payments.map((p) => (
          <div key={p.debt_id} className="flex items-center justify-between rounded-2xl pe-surface p-4">
            <p className="font-medium text-[#171717]">{p.name}</p>
            {editing ? (
              <input inputMode="decimal" value={p.actual_amount} onChange={(e) => setAmt(p.debt_id, e.target.value)} data-testid={`ml-pay-${p.debt_id}`} className="w-32 rounded-xl border border-[#E5E0D9] px-3 py-2 text-right text-[#171717] outline-none focus:border-[#2F6B57]" />
            ) : (
              <p className="font-semibold text-[#164C3B]">{fmt(parseFloat(p.actual_amount) || 0)}</p>
            )}
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between text-sm">
        <button type="button" onClick={() => setEditing((v) => !v)} data-testid="ml-different-amount" className="font-medium text-[#2F6B57] hover:underline">
          {editing ? "Done editing" : "I paid a different amount"}
        </button>
        <p className="text-[#70584A]">Total: <span className="font-semibold text-[#171717]">{fmt(total)}</span></p>
      </div>

      <p className="mt-5 rounded-xl bg-[#F5F2EC] p-3 text-sm text-[#70584A]">Only a payment you confirm here updates your Paycheck Edit balances. The Paycheck Edit does not move money or contact your lender.</p>

      <div className="mt-5 space-y-3">
        <Button data-testid="ml-made-payment" disabled={busy} onClick={onConfirm} className="min-h-12 w-full rounded-xl bg-[#2F6B57] text-base font-semibold tracking-wide text-white hover:bg-[#255643]">I MADE THIS MONEY MOVE</Button>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Button data-testid="ml-kept-protected" variant="outline" disabled={busy} onClick={onProtect} className="min-h-11 rounded-xl border-[#E5E0D9] text-[#164C3B] hover:bg-[#F5F2EC]">I kept it protected</Button>
          <Button data-testid="ml-not-yet" variant="ghost" disabled={busy} onClick={onLater} className="min-h-11 rounded-xl text-[#70584A] hover:bg-[#F5F2EC]">Not yet / I'll do it later</Button>
        </div>
        <button type="button" data-testid="ml-change-move" disabled={busy} onClick={onChangeMove} className="mx-auto block text-sm font-medium text-[#8A7C6B] underline decoration-[#D6CBB8] underline-offset-4 hover:text-[#70584A]">
          Plans changed? Change my move
        </button>
      </div>
    </div>
  );
}

// What If / "What can $X do?" A lightweight, NON-persisting hypothetical. The
// simulation is computed by the canonical engine on the backend (/sprint/simulate
// writes nothing). Abandoning it (NEVER MIND) changes nothing. The only path
// toward real money activity is MAKE THIS MY MOVE, which starts a fresh real
// calculate and still runs the normal recommend -> accept -> confirm workflow.
function WhatIf({ amount, setAmount, plan, onRun, onMakeMove, onNeverMind, onReset, busy }) {
  const b = plan?.foundation_breakdown || {};
  const prot = b.protection || 0;
  const pot = plan?.potential_sprint_money || 0;
  const smallEdit = pot > 0 && pot <= SMALL_EDIT_MAX;
  return (
    <div data-testid="ml-whatif">
      <PageHead title={<>What if? </>} sub="Try any amount and see what the move would be. This is a preview only. Nothing is saved, nothing changes, and your real plan stays exactly as it is." />
      <form onSubmit={(e) => { e.preventDefault(); onRun(); }} className="rounded-2xl pe-surface p-6">
        <label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]">What if this much showed up?</label>
        <input inputMode="decimal" value={amount} data-testid="ml-whatif-input" onChange={(e) => { setAmount(e.target.value.replace(/[^0-9.]/g, "")); if (plan) onReset(); }} className={input} placeholder="$0" />
        <p className="mt-2 text-xs text-[#A89A88]">Enter any amount you are curious about. We will show what could happen without touching anything.</p>
        <Button type="submit" data-testid="ml-whatif-run" disabled={busy} className="mt-4 min-h-12 w-full rounded-xl bg-[#2F6B57] font-semibold text-white hover:bg-[#255643]">
          {busy ? "Running…" : "See what it could do"} <Sparkles className="ml-1.5 h-4 w-4" />
        </Button>
      </form>

      {plan && (
        <div className="mt-5" data-testid="ml-whatif-result">
          <div className="rounded-2xl border border-[#DDE7DF] bg-[#DDE7DF] p-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9A7B3A]">Hypothetical preview</p>
            {smallEdit && <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#9A7B3A]" data-testid="ml-whatif-small-edit">Small edit. Still counts.</p>}
            <p className="mt-1 text-xs font-semibold uppercase tracking-[0.14em] text-[#164C3B]">Estimated available</p>
            <p className="mt-1 font-serif text-4xl text-[#164C3B]" data-testid="ml-whatif-potential">{fmt(pot)}</p>
            <p className="mt-1 text-sm text-[#2F6B57]">From {fmt(b.income_received)} landed, after {fmt(b.total_obligations)} reserved for real life{prot > 0 ? ` and ${fmt(prot)} protected` : ""}.</p>
          </div>
          <div className="mt-3 rounded-2xl pe-surface p-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8A7C6B]">Why this move</p>
            <p className="mt-1.5 text-sm leading-relaxed text-[#525252]" data-testid="ml-whatif-why">{whyThisMove(plan)}</p>
          </div>
          <div className="mt-5 rounded-xl bg-[#F5F2EC] p-3 text-sm text-[#70584A]" data-testid="ml-whatif-safe">
            This is only a preview. Nothing has been saved or changed. Choose MAKE THIS MY MOVE to start the real flow, where you still review, accept, and confirm before anything counts.
          </div>
          <div className="mt-4 space-y-3">
            <Button data-testid="ml-whatif-make-move" disabled={busy} onClick={onMakeMove} className="min-h-12 w-full rounded-xl bg-[#2F6B57] text-base font-semibold text-white hover:bg-[#255643]">MAKE THIS MY MOVE</Button>
            <Button data-testid="ml-whatif-never-mind" variant="ghost" disabled={busy} onClick={onNeverMind} className="min-h-11 w-full rounded-xl text-[#70584A] hover:bg-[#F5F2EC]">NEVER MIND</Button>
          </div>
        </div>
      )}
      {!plan && (
        <button type="button" data-testid="ml-whatif-exit" onClick={onNeverMind} className="mx-auto mt-5 block text-sm font-medium text-[#70584A] hover:underline">NEVER MIND</button>
      )}
    </div>
  );
}
